import { useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Stack } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useAutoConnectCamera from 'src/hooks/useAutoConnectCamera';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';
import useValidatePassword from 'src/hooks/useValidatePassword';

export interface ResetPasswordFormData {
  otp: string;
  password: string;
  confirmPassword: string;
}

interface ResetPasswordFormProps {
  userId: string;
  currentPassword: string;
}

const ResetPasswordForm = ({
  userId,
  currentPassword,
}: ResetPasswordFormProps) => {
  const { tPages, tCommon, language } = useUiTranslation();
  const history = useHistory();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const { currentUser, practiceSettings } = useRemoteBoundStore();

  const { setRememberUserEmail, isRememberEmail } = useLocalStore();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      otp: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const password = watch('password');

  const { autoConnectCamera } = useAutoConnectCamera();

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await Api.remote.change_password(
        userId,
        currentPassword,
        data.password.trim(),
      );

      autoConnectCamera(practiceSettings);
      if (isRememberEmail) {
        setRememberUserEmail(currentUser?.emailAddress);
      } else {
        setRememberUserEmail(undefined);
      }

      history.replace('/');
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit(handleResetPassword)}>
      <Modal.Body>
        <Stack className="text-center" gap={2}>
          <h2>{tPages('changePassword.title')}</h2>
          <Alert variant="warning">
            {tPages('loginLogout.passwordExpired')}
          </Alert>
        </Stack>

        <Form.Group className="mb-2">
          <Form.Label>
            <strong>{tPages('changePassword.newPassword')}</strong>
          </Form.Label>
          <InputGroup>
            <Form.Control
              type={isShowPassword ? 'text' : 'password'}
              placeholder={tPages('changePassword.newPasswordPlaceholder')}
              autoComplete="off"
              {...register('password', {
                required: tPages('changePassword.passwordRequired'),
                validate: useValidatePassword(),
              })}
            />
            <InputGroup.Text
              onClick={() => setIsShowPassword(!isShowPassword)}
              className="pointer"
            >
              <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
            </InputGroup.Text>
          </InputGroup>
          <div className="text-danger">
            {errors.password?.type === 'required' ? (
              <p>{tPages('changePassword.newPasswordRequired')}</p>
            ) : (
              <ul className="list-unstyled">
                {errors.password?.types?.minLength && (
                  <li>{errors.password.message}</li>
                )}
                {errors.password?.types?.hasUpperCase && (
                  <li>{errors.password.types.hasUpperCase}</li>
                )}
                {errors.password?.types?.hasLowerCase && (
                  <li>{errors.password.types.hasLowerCase}</li>
                )}
                {errors.password?.types?.hasNumber && (
                  <li>{errors.password.types.hasNumber}</li>
                )}
                {errors.password?.types?.hasSpecialChar && (
                  <li>{errors.password.types.hasSpecialChar}</li>
                )}
              </ul>
            )}
          </div>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>
            <strong>{tPages('changePassword.confirmPassword')}</strong>
          </Form.Label>
          <InputGroup>
            <Form.Control
              type={isShowConfirmPassword ? 'text' : 'password'}
              placeholder={tPages('changePassword.confirmPasswordPlaceholder')}
              autoComplete="off"
              {...register('confirmPassword', {
                required: tPages('changePassword.confirmPasswordRequired'),
                validate: (value) =>
                  value === password ||
                  tPages('changePassword.passwordsDoNotMatch'),
              })}
            />
            <InputGroup.Text
              onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
              className="pointer"
            >
              <FontAwesomeIcon
                icon={isShowConfirmPassword ? faEye : faEyeSlash}
              />
            </InputGroup.Text>
          </InputGroup>
          {<p className="text-danger ">{errors.confirmPassword?.message}</p>}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="submit" disabled={isSubmitting}>
          {tPages('changePassword.change')}
        </Button>
      </Modal.Footer>
    </Form>
  );
};

export default ResetPasswordForm;
